import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiCreditCard, FiSave } from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';
import { appAlert } from '../utils/appDialog';

const PAYMENT_MODES = [
  { value: '', label: '— Not set —' },
  { value: 'bank', label: 'Bank transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'wire', label: 'Wire' },
  { value: 'other', label: 'Other' },
];

const emptyForm = {
  billing_name: '',
  billing_email: '',
  billing_phone: '',
  billing_address: '',
  tax_id: '',
  tax_id_type: '',
  state_province: '',
  country_code: '',
  preferred_payment_mode: '',
  payment_method_notes: '',
  bank_name: '',
  bank_account_last4: '',
  bank_ifsc_or_routing: '',
  upi_or_paypal: '',
  notes: '',
};

/**
 * Billing / payment details on a candidate profile.
 * Used as bill-to + payment mode when recording package payments (auto invoice).
 */
export default function CandidatePaymentProfilePanel({ candidateId, candidate = {}, canEdit = false }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);

  const { data, isLoading } = useQuery(
    ['candidate-payment-profile', candidateId],
    () => api.get(`/services/candidates/${candidateId}/payment-profile`).then((r) => r.data),
    { enabled: !!candidateId }
  );

  useEffect(() => {
    if (!data) return;
    setForm({
      billing_name:
        data.billing_name ||
        `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() ||
        '',
      billing_email: data.billing_email || candidate.email || '',
      billing_phone: data.billing_phone || candidate.phone || '',
      billing_address: data.billing_address || '',
      tax_id: data.tax_id || '',
      tax_id_type: data.tax_id_type || '',
      state_province: data.state_province || '',
      country_code: data.country_code || '',
      preferred_payment_mode: data.preferred_payment_mode || '',
      payment_method_notes: data.payment_method_notes || '',
      bank_name: data.bank_name || '',
      bank_account_last4: data.bank_account_last4 || '',
      bank_ifsc_or_routing: data.bank_ifsc_or_routing || '',
      upi_or_paypal: data.upi_or_paypal || '',
      notes: data.notes || '',
    });
  }, [data, candidate.first_name, candidate.last_name, candidate.email, candidate.phone]);

  const saveMutation = useMutation(
    (payload) => api.put(`/services/candidates/${candidateId}/payment-profile`, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['candidate-payment-profile', candidateId]);
        void appAlert('Payment details saved. They will be used on package payment invoices.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  if (isLoading) return <div className="loading">Loading payment details…</div>;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <section className="candidate-section candidate-details-card candidate-payment-profile">
      <div className="section-header candidate-services-head">
        <h2>
          <FiCreditCard /> Payment details
        </h2>
      </div>
      <p className="text-muted" style={{ marginTop: 0 }}>
        Billing identity and preferred payment method for this candidate. When a payment is recorded
        against a service package, finance automatically creates a sales invoice using these details.
      </p>
      <form
        className="svc-form-grid svc-form-grid--2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canEdit) return;
          saveMutation.mutate(form);
        }}
      >
        <div className="form-group">
          <label>Billing name</label>
          <input
            value={form.billing_name}
            onChange={(e) => setField('billing_name', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>Billing email</label>
          <input
            type="email"
            value={form.billing_email}
            onChange={(e) => setField('billing_email', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>Billing phone</label>
          <input
            value={form.billing_phone}
            onChange={(e) => setField('billing_phone', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>Preferred payment mode</label>
          <select
            value={form.preferred_payment_mode}
            onChange={(e) => setField('preferred_payment_mode', e.target.value)}
            disabled={!canEdit}
          >
            {PAYMENT_MODES.map((m) => (
              <option key={m.value || 'none'} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Billing address</label>
          <textarea
            rows={2}
            value={form.billing_address}
            onChange={(e) => setField('billing_address', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>Tax ID</label>
          <input
            value={form.tax_id}
            onChange={(e) => setField('tax_id', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>Tax ID type</label>
          <input
            value={form.tax_id_type}
            onChange={(e) => setField('tax_id_type', e.target.value)}
            placeholder="GSTIN, PAN, EIN…"
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>State / province</label>
          <input
            value={form.state_province}
            onChange={(e) => setField('state_province', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>Country code</label>
          <input
            value={form.country_code}
            onChange={(e) => setField('country_code', e.target.value.toUpperCase())}
            placeholder="IN, US, CA"
            maxLength={3}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>Bank name</label>
          <input
            value={form.bank_name}
            onChange={(e) => setField('bank_name', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>Account last 4</label>
          <input
            value={form.bank_account_last4}
            onChange={(e) => setField('bank_account_last4', e.target.value.replace(/\D/g, '').slice(0, 4))}
            maxLength={4}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>IFSC / routing</label>
          <input
            value={form.bank_ifsc_or_routing}
            onChange={(e) => setField('bank_ifsc_or_routing', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group">
          <label>UPI / PayPal</label>
          <input
            value={form.upi_or_paypal}
            onChange={(e) => setField('upi_or_paypal', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Payment method notes</label>
          <textarea
            rows={2}
            value={form.payment_method_notes}
            onChange={(e) => setField('payment_method_notes', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        {canEdit ? (
          <div className="modal-actions" style={{ gridColumn: '1 / -1' }}>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={saveMutation.isLoading}
              icon={FiSave}
            >
              Save payment details
            </LoadingButton>
          </div>
        ) : null}
      </form>
    </section>
  );
}
